import { DialogContent, styled, useTheme } from '@mui/material';
import { StyledLink } from 'FeatureModules/Activity/Components/Forms/styles';
import useConfig from 'hooks/useConfig';
import useDialog from 'hooks/useDialog';
import { InlineHelperText } from 'FeatureModules/ProductPage/Components/InlineHelperText/InlineHelperText';
import { DialogDefault, ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import FlexBox from 'Components/FlexBox';
import SeparatorLine from 'Components/SeparatorLine';

interface ICreateDatasetDecisionDialogProps extends IDefaultDialogProps {
  datasetOwnedBy?: IBasePartnerCompany;
  /**
   * The ID of the cluster to which the dataset should add the sites
   */
  defaultClusterId?: string;
}

const HelperTextWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const CreateDatasetDecisionDialog: FC<ICreateDatasetDecisionDialogProps> = ({
  open,
  datasetOwnedBy,
  defaultClusterId,
  onClose,
}) => {
  const { geoDataTemplateURL } = useConfig();
  const { openDialog } = useDialog();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleUploadGeoDataClick = () => {
    openDialog({
      type: 'DATASET_IMPORT',
      props: { ownedBy: datasetOwnedBy, defaultClusterId },
    });
    onClose?.();
  };

  const handleManualEnterClick = () => {
    // Passing
    navigate(
      `${location.pathname}/datasets${defaultClusterId ? `?clusterId=${defaultClusterId}` : ''}`
    );
    onClose?.();
  };

  return (
    <DialogDefault
      open={open}
      title="New dataset"
      onClose={onClose}
      maxWidth="xs"
      data-testid="decision-dialog"
    >
      <DialogContent>
        <ThemeButton
          color="PRIMARY"
          size="large"
          fullWidth
          data-testid="select-partner-button"
          startIcon={<Icon name="upload" color="WHITE" />}
          onClick={handleUploadGeoDataClick}
        >
          Upload geo-data file
        </ThemeButton>
        <FlexBox mt={2} gap={0.5}>
          <ThemeTypography color="GRAY_60" variant="BODY_SMALL">
            Upload origin geo-data by .csv .xlsx .kml or .geojson.
            <StyledLink to={geoDataTemplateURL}>
              {' '}
              Download optimal file template examples.
            </StyledLink>
          </ThemeTypography>
        </FlexBox>
        <SeparatorLine label="or" width="80%" backgroundColor={theme.custom.colors.offWhite} />

        <ThemeButton
          color="BLUE_ICE"
          size="large"
          fullWidth
          data-testid="add-new-activity-button"
          onClick={handleManualEnterClick}
        >
          Manually enter geo-data
        </ThemeButton>

        <HelperTextWrapper>
          <InlineHelperText
            variant="INFO"
            helperText="Add geo-data by copy & pasting into the table or manually entering numbers"
          />
        </HelperTextWrapper>
      </DialogContent>
    </DialogDefault>
  );
};

export default CreateDatasetDecisionDialog;
